/*
 * Copyright (c) 2026 Centrica.
 *
 */

/**
 * Claves de la sesion que dependen de la compania elegida: los tokens, su vencimiento, los datos
 * del usuario (que traen la compania y los roles) y el nombre de la compania.
 */
export const CLAVES_SESION_PESTANA = [
  'access_token',
  'refresh_token',
  'token_expiry',
  'user_data',
  'companyName',
] as const;

/** Una de las claves de la sesion de la pestaña. */
export type ClaveSesionPestana = (typeof CLAVES_SESION_PESTANA)[number];

/**
 * Marca de la pestaña que cerro sesion: ya no hereda la sesion de las otras hasta que vuelva a
 * entrar. Sin ella, al volver a la entrada tomaria la compania de otra pestaña sin que nadie la
 * eligiera.
 */
const SESION_CERRADA = 'sesion_cerrada_en_pestana';

/**
 * Lee la sesion DE ESTA PESTAÑA.
 *
 * <p>La sesion vivia solo en `localStorage`, que comparten todas las pestañas del navegador: si una
 * pestaña elegia otra compania, las demas pasaban a mostrar su nombre y a refrescar con su token,
 * y una pestaña podia pintar el nombre de una compania con los datos de otra. Cada pestaña muestra
 * y usa SOLO el tenant de su propio token (Carlos, 23-oct: «debe respetar el tenant»).</p>
 *
 * <p>Mismo patron que el producto activo (Mantis #455): la fuente es `sessionStorage`, que es de
 * cada pestaña y sobrevive a la recarga. `localStorage` solo da el valor inicial: una pestaña nueva
 * arranca con la ultima compania elegida y, desde ahi, lleva la suya.</p>
 *
 * @param clave la clave de la sesion.
 * @returns el valor de la pestaña, el heredado al abrirla o `null` si no hay sesion.
 */
export function leerDeLaPestana(clave: ClaveSesionPestana): string | null {
  heredarSiHaceFalta();
  return sessionStorage.getItem(clave);
}

/**
 * La primera vez que la pestaña no tiene sesion propia (y no la cerro), toma DE UNA VEZ las cinco
 * claves de la ultima compania elegida en el navegador. Heredarlas una a una, cuando se piden,
 * podia dejar el token de una compania con los datos de otra si entre medias otra pestaña cambiaba
 * de compania.
 */
function heredarSiHaceFalta(): void {
  if (sessionStorage.getItem('access_token') !== null) return;
  if (sessionStorage.getItem(SESION_CERRADA) !== null) return;
  for (const clave of CLAVES_SESION_PESTANA) {
    const heredado = localStorage.getItem(clave);
    if (heredado !== null && sessionStorage.getItem(clave) === null) {
      sessionStorage.setItem(clave, heredado);
    }
  }
}

/**
 * Fija el valor en esta pestaña y lo deja como valor inicial de las que se abran despues.
 *
 * @param clave la clave de la sesion.
 * @param valor su valor.
 */
export function guardarEnLaPestana(clave: ClaveSesionPestana, valor: string): void {
  sessionStorage.removeItem(SESION_CERRADA);
  sessionStorage.setItem(clave, valor);
  localStorage.setItem(clave, valor);
}

/**
 * Quita la clave en esta pestaña. El valor inicial compartido solo se quita si esta pestaña tiene
 * uno y sigue siendo el suyo: si otra eligio despues otra compania, o si esta no tenia sesion, ese
 * valor es de otra y se conserva. Quitar
 * una clave sola no cierra la sesion de la pestaña.
 *
 * @param clave la clave de la sesion.
 */
export function quitarDeLaPestana(clave: ClaveSesionPestana): void {
  const propio = sessionStorage.getItem(clave);
  sessionStorage.removeItem(clave);
  // Sin valor propio no hay nada suyo que quitar: una pestaña sin sesion (la de la entrada, que
  // limpia al abrirse) borraba asi la sesion que otra pestaña habia dejado.
  if (propio !== null && localStorage.getItem(clave) === propio) {
    localStorage.removeItem(clave);
  }
}

/**
 * Cierra la sesion DE ESTA PESTAÑA: quita sus cinco claves y deja la marca, de modo que no herede
 * la sesion de las otras hasta que vuelva a entrar. Solo lo hace la salida.
 */
export function cerrarSesionDeLaPestana(): void {
  CLAVES_SESION_PESTANA.forEach((clave) => quitarDeLaPestana(clave));
  sessionStorage.setItem(SESION_CERRADA, '1');
}

/**
 * Valor inicial que dejo la ultima pestaña que eligio compania, sin adoptarlo en esta. Sirve para
 * comparar, nunca para usarlo como propio.
 *
 * @param clave la clave de la sesion.
 * @returns el valor compartido o `null`.
 */
export function leerDeOtraPestana(clave: ClaveSesionPestana): string | null {
  return localStorage.getItem(clave);
}
